/**
 * usePeriodNavigation — shared hook for PeriodBar date navigation.
 *
 * Provides anchor date state, navigation handlers, formatted label,
 * and ready-made `navigation` prop for PeriodBar.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLocale } from '@/lib/utils';
import { periodWindowLabel } from '@/lib/period-label';
import { startOfWeek, type WeekStartsOn } from '@aica/core';
import { useWeekStartsOn } from '@/hooks/useWeekStartsOn';
import type { Period, PeriodNavigationProps } from '@/components/layout/PeriodBar';

// ============================================================================
// Date range calculation
// ============================================================================

export interface PeriodDateRange {
  start: Date;
  end: Date;
  /** ISO string for API calls */
  periodStart: string;
  /** ISO string for API calls */
  periodEnd: string;
}

/**
 * @param weekStartsOn — the organization's first day of the week (#864,
 *   `useWeekStartsOn`); only the Week period reads it.
 */
export function getPeriodDateRange(
  period: Period,
  anchorDate: Date,
  weekStartsOn: WeekStartsOn
): PeriodDateRange {
  let start: Date;
  let end: Date;

  switch (period) {
    case 'day':
    case 'today': {
      start = new Date(anchorDate);
      start.setHours(0, 0, 0, 0);
      end = new Date(anchorDate);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case 'week': {
      start = startOfWeek(anchorDate, weekStartsOn);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case 'month': {
      start = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
      end = new Date(
        anchorDate.getFullYear(),
        anchorDate.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
      break;
    }
    case 'quarter': {
      const qMonth = Math.floor(anchorDate.getMonth() / 3) * 3;
      start = new Date(anchorDate.getFullYear(), qMonth, 1);
      end = new Date(anchorDate.getFullYear(), qMonth + 3, 0, 23, 59, 59, 999);
      break;
    }
  }

  return {
    start,
    end,
    periodStart: start.toISOString(),
    periodEnd: end.toISOString(),
  };
}

// ============================================================================
// Navigation helpers
// ============================================================================

function shiftDate(period: Period, date: Date, direction: -1 | 1): Date {
  const next = new Date(date);
  switch (period) {
    case 'day':
    case 'today':
      next.setDate(date.getDate() + direction);
      break;
    case 'week':
      next.setDate(date.getDate() + direction * 7);
      break;
    case 'month':
      next.setMonth(date.getMonth() + direction);
      break;
    case 'quarter':
      next.setMonth(date.getMonth() + direction * 3);
      break;
  }
  return next;
}

function formatPeriodLabel(
  period: Period,
  date: Date,
  locale: string,
  weekStartsOn: WeekStartsOn
): string {
  switch (period) {
    case 'day':
    case 'today':
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    case 'week': {
      const weekStart = startOfWeek(date, weekStartsOn);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${weekEnd.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}`;
    }
    case 'month':
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    case 'quarter': {
      const qMonth = Math.floor(date.getMonth() / 3) * 3;
      const qStart = new Date(date.getFullYear(), qMonth, 1);
      const qEnd = new Date(date.getFullYear(), qMonth + 2, 1);
      return `${qStart.toLocaleDateString(locale, { month: 'short' })} – ${qEnd.toLocaleDateString(locale, { month: 'short', year: 'numeric' })}`;
    }
  }
}

function isCurrentPeriod(
  period: Period,
  date: Date,
  weekStartsOn: WeekStartsOn
): boolean {
  const now = new Date();
  switch (period) {
    case 'day':
    case 'today':
      return (
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate()
      );
    case 'week': {
      const startCurrent = startOfWeek(now, weekStartsOn);
      const startSelected = startOfWeek(date, weekStartsOn);
      return (
        startCurrent.getFullYear() === startSelected.getFullYear() &&
        startCurrent.getMonth() === startSelected.getMonth() &&
        startCurrent.getDate() === startSelected.getDate()
      );
    }
    case 'month':
      return (
        date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
      );
    case 'quarter': {
      const nowQ = Math.floor(now.getMonth() / 3);
      const dateQ = Math.floor(date.getMonth() / 3);
      return date.getFullYear() === now.getFullYear() && dateQ === nowQ;
    }
  }
}

// ============================================================================
// Hook
// ============================================================================

export interface UsePeriodNavigationReturn {
  /** Current anchor date for navigation */
  anchorDate: Date;
  /** Set anchor date directly */
  setAnchorDate: (date: Date) => void;
  /** Date range for the current period + anchor */
  dateRange: PeriodDateRange;
  /** Ready-made navigation props for PeriodBar */
  navigation: PeriodNavigationProps;
}

/**
 * @param control — the anchor owned by the caller (the address of a screen,
 *   #846) instead of this hook's state: the arrows and the reset call
 *   `onAnchorChange`, and the caller resets the anchor when the period tab
 *   changes. Without it the hook keeps its own anchor, as before.
 * @param options.weekStartsOn — the first day of the week this screen draws,
 *   instead of the selected organization's. For a screen that is not about one
 *   organization — the platform-wide admin report — whose week must not move
 *   with whichever org the operator happens to have selected (#864).
 */
export function usePeriodNavigation(
  period: Period,
  control?: { anchor: Date; onAnchorChange: (date: Date) => void },
  options?: { weekStartsOn?: WeekStartsOn }
): UsePeriodNavigationReturn {
  const { t } = useTranslation('common');
  const locale = resolveLocale();
  const orgWeekStartsOn = useWeekStartsOn();
  const weekStartsOn = options?.weekStartsOn ?? orgWeekStartsOn;
  const [ownAnchor, setOwnAnchor] = useState<Date>(() => new Date());
  const anchorDate = control?.anchor ?? ownAnchor;
  const onAnchorChange = control?.onAnchorChange;
  const setAnchorDate = useCallback(
    (date: Date) => (onAnchorChange ? onAnchorChange(date) : setOwnAnchor(date)),
    [onAnchorChange]
  );

  // Reset anchor to today when period tab changes (e.g. week → month). A
  // controlled anchor is reset by its owner, in the same update as the period.
  const controlled = Boolean(control);
  useEffect(() => {
    if (!controlled) setOwnAnchor(new Date());
  }, [period, controlled]);

  const dateRange = useMemo(
    () => getPeriodDateRange(period, anchorDate, weekStartsOn),
    [period, anchorDate, weekStartsOn]
  );

  const isCurrent = useMemo(
    () => isCurrentPeriod(period, anchorDate, weekStartsOn),
    [period, anchorDate, weekStartsOn]
  );

  const handlePrev = useCallback(
    () => setAnchorDate(shiftDate(period, anchorDate, -1)),
    [period, anchorDate, setAnchorDate]
  );
  const handleNext = useCallback(
    () => setAnchorDate(shiftDate(period, anchorDate, 1)),
    [period, anchorDate, setAnchorDate]
  );
  const handleReset = useCallback(() => setAnchorDate(new Date()), [setAnchorDate]);

  const navigation: PeriodNavigationProps = useMemo(
    () => ({
      label: formatPeriodLabel(period, anchorDate, locale, weekStartsOn),
      onPrev: handlePrev,
      onNext: handleNext,
      prevAriaLabel: t('common:periodNav.prevPeriod', 'Previous period'),
      nextAriaLabel: t('common:periodNav.nextPeriod', 'Next period'),
      ...(!isCurrent && {
        resetLabel: periodWindowLabel(period, t),
        onReset: handleReset,
      }),
    }),
    [
      period,
      anchorDate,
      locale,
      weekStartsOn,
      isCurrent,
      handlePrev,
      handleNext,
      handleReset,
      t,
    ]
  );

  // `isCurrent` наружу не отдаётся: он питает кнопку сброса внутри навигации.
  // Отдавали его ради строки «осталось бесплатных минут» на экране биллинга —
  // а остаток переехал в карточку, которая про «сейчас» и от периода не
  // зависит вовсе, так что читателей у поля не осталось.
  return { anchorDate, setAnchorDate, dateRange, navigation };
}
